import type { ReactElement } from 'react';
import { SurveyStats, SurveyStatus, SurveySummaryData } from 'src/redux/survey/survey.types';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import OverviewSidepanelQuestion from './overview-sidepanel-question';
import OverviewSidepanelSummary from './overview-sidepanel-summary';
import {
  buildDemographicComposition,
  demographicAttributeLabel,
  getSurveyUserBucket,
  type RespondentScope,
} from '../../services/demographic-breakout.helpers';

interface OverviewSidePanelProps {
  stats: SurveyStats;
  breakoutAttribute: string | null;
  respondentScope: RespondentScope;
  activeQuestionIndex: number | null;
  onQuestionSelect: (index: number) => void;
  summary?: SurveySummaryData;
  showSummaries: boolean;
  surveyStatus?: SurveyStatus;
  isShare?: boolean;
  isGeneratingSummary?: boolean;
  isLoadingSummary?: boolean;
  hasCreativeAnswers?: boolean;
  onGenerateSummary?: () => void;
  onFocusGroupSelect?: () => void;
  isFocusGroupActive?: boolean;
}

function StatBlock({
  label,
  value,
}: {
  label: string;
  value: string | number;
}): ReactElement {
  return (
    <div className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-3 py-2.5">
      <div className={portalDs.sectionLabel}>
        {label}
      </div>
      <div className="mt-1 text-xl font-bold tracking-[-0.5px] text-[#0D0F1A]">
        {value}
      </div>
    </div>
  );
}

function OverviewSidePanel({
  stats,
  breakoutAttribute,
  respondentScope,
  activeQuestionIndex,
  onQuestionSelect,
  summary,
  showSummaries,
  surveyStatus,
  isShare,
  isGeneratingSummary,
  isLoadingSummary,
  hasCreativeAnswers,
  onGenerateSummary,
  onFocusGroupSelect,
  isFocusGroupActive = false,
}: OverviewSidePanelProps): ReactElement {
  const totalUsers = stats.total_users;
  const aiUsers = getSurveyUserBucket(stats, 'ai_users');
  const humanUsers = getSurveyUserBucket(stats, 'human_users');
  const composition = breakoutAttribute
    ? buildDemographicComposition(stats, breakoutAttribute, respondentScope)
    : [];

  return (
    <aside className="w-full max-w-[320px] shrink-0 overflow-y-auto border-l border-[#EDEEF2] bg-white p-6">
      <h2 className={cn(portalDs.sectionTitle, 'mb-5 text-lg')}>
        Overview
      </h2>

      <OverviewSidepanelSummary
        summary={summary}
        showSummaries={showSummaries}
        surveyStatus={surveyStatus}
        isShare={isShare}
        isGeneratingSummary={isGeneratingSummary}
        isLoadingSummary={isLoadingSummary}
        hasCreativeAnswers={hasCreativeAnswers}
        onGenerateSummary={onGenerateSummary}
      />

      <div className="mb-6 grid grid-cols-2 gap-2">
        <StatBlock
          label="Total respondents"
          value={totalUsers?.total ?? stats.totalUsers}
        />
        <StatBlock
          label="Started"
          value={totalUsers?.started ?? stats.totalUsers}
        />
        <StatBlock
          label="Completed"
          value={totalUsers?.completed ?? stats.totalUsers}
        />
        <StatBlock
          label="AI started"
          value={aiUsers?.started ?? stats.aiUsers}
        />
        <StatBlock
          label="Human started"
          value={humanUsers?.started ?? stats.humanUsers}
        />
      </div>

      {aiUsers?.raw_n != null ? (
        <div className={cn(portalDs.alertBase, portalDs.alertInfo, 'mb-6 px-3 py-2.5')}>
          <div>
            <div className={portalDs.alertTitle}>
              Survey AI sample quality
            </div>
            <div className={cn(portalDs.alertBody, 'mt-1')}>
              Raw N
              {' '}
              {aiUsers.raw_n}
              {aiUsers.effective_n != null ? ` · Effective N ${Math.round(aiUsers.effective_n)}` : ''}
              {aiUsers.icc != null ? ` · ICC ${Number(aiUsers.icc).toFixed(2)}` : ''}
            </div>
          </div>
        </div>
      ) : null}

      <h3 className={cn(portalDs.filterLabel, 'mb-2')}>
        Demographic composition
      </h3>
      {breakoutAttribute ? (
        composition.length > 0 ? (
          <ul className="mb-6 space-y-1.5">
            {composition.map((segment) => (
              <li
                key={segment.key}
                className="flex items-center justify-between gap-2"
              >
                <span className={cn(portalDs.bodySm, 'truncate text-[#0D0F1A]')}>
                  {segment.label}
                </span>
                <span className={cn(portalDs.caption, 'shrink-0')}>
                  n=
                  {segment.count}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={cn(portalDs.bodySm, 'mb-6')}>
            No composition data for
            {' '}
            {demographicAttributeLabel(breakoutAttribute)}
            .
          </p>
        )
      ) : (
        <p className={cn(portalDs.bodySm, 'mb-6')}>
          Select a breakout attribute to see demographic composition.
        </p>
      )}

      {onFocusGroupSelect ? (
        <div className="mb-6">
          <h3 className={cn(portalDs.filterLabel, 'mb-2')}>
            Sections
          </h3>
          <button
            type="button"
            className={cn(
              'flex w-full items-start rounded-lg px-2 py-2 text-left transition-colors hover:bg-[#F0F1F5]',
              isFocusGroupActive && 'bg-[#EFF6FF]',
            )}
            onClick={onFocusGroupSelect}
          >
            <span className={cn(portalDs.bodySm, 'font-semibold text-[#0D0F1A]')}>
              Focus group
            </span>
          </button>
        </div>
      ) : null}

      <h3 className={cn(portalDs.filterLabel, 'mb-2')}>
        Questions
      </h3>
      <div className="space-y-1">
        {stats.questions.map((question, index) => (
          <OverviewSidepanelQuestion
            key={question.id}
            index={index}
            question={question}
            isActive={activeQuestionIndex === index}
            onClick={() => onQuestionSelect(index)}
          />
        ))}
      </div>
    </aside>
  );
}

export default OverviewSidePanel;
